"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePostHogEvent = usePostHogEvent;
var react_1 = require("react");
var react_2 = require("@posthog/react");
// useVideoState is a video-player helper that lives with the rest of the
// VideoJS accessors in OracleCommons (it's not PostHog-specific). The PostHog
// hook imports it to enrich the in-video-guide Layer 3 context with
// `playing_content_id` and `playing_feature_media_id` (the `playing_` prefix
// avoids collision with the recommendation events' `content_id` payload).
var useVideoState_1 = require("components/OracleCommons/useVideoState");
var PostHogProvider_1 = require("./PostHogProvider");
// Per-feature event-callback factories. Each lives next to its event-type
// definition under `events/<Feature>/` and returns the slice of the callback
// map for that feature.
var chat_1 = require("./events/AI/chat");
var moderation_1 = require("./events/AI/moderation");
var channels_1 = require("./events/Oracle/channels");
var divination_1 = require("./events/Oracle/divination");
var playback_1 = require("./events/VideoPlayer/playback");
var header_1 = require("./events/Community/header");
var pageViews_1 = require("./events/Community/pageViews");
var profile_1 = require("./events/Community/profile");
var playlistLibrary_1 = require("./events/Community/playlistLibrary");
var discover_1 = require("./events/Community/discover");
var sharedLink_1 = require("./events/Community/sharedLink");
/**
 * Type-safe hook for tracking PostHog events.
 * Returns a callback with the correct signature for the specified event.
 * Automatically includes context, playing_content_id, playing_feature_media_id,
 * and other common properties.
 *
 * Callbacks are stable across renders — fresh values are read from a ref so
 * callers can wrap usage in useCallback without invalidating other memos.
 *
 * Event-name typing comes from `events/<Feature>/`; each feature folder owns
 * its slice of `PostHogEventSignatures` and now also owns its slice of the
 * runtime callback map (via `create<Feature>EventCallbacks`).
 *
 * @example
 * const trackMessageSent = usePostHogEvent('ai_message_sent')
 * // Later, at call site:
 * trackMessageSent(message, 'Text', 'keyboard')
 */
// More named exports may be added (selectors, helpers); a default export
// would force consumers into anonymous imports.
// eslint-disable-next-line import/prefer-default-export
function usePostHogEvent(event) {
    var posthogClient = (0, react_2.usePostHog)();
    var eventContext = (0, react_1.useContext)(PostHogProvider_1.EventContext);
    // `useVideoState` keeps its native field names; the rename to
    // `playingContentId` / `playingFeatureMediaId` happens here so consumers of
    // the callback context speak the Layer 3 vocabulary.
    var _a = (0, useVideoState_1.useVideoState)(), contentId = _a.contentId, featureMediaId = _a.featureMediaId, getPlaybackState = _a.getPlaybackState;
    var _b = (0, PostHogProvider_1.useChatSessionContext)(), chatSessionId = _b.chatSessionId, conversationTitle = _b.conversationTitle, aiPersona = _b.aiPersona, chatExperienceName = _b.chatExperienceName;
    // Store latest values in a ref so callbacks can access fresh data without
    // needing to be recreated when dependencies change.
    var contextRef = (0, react_1.useRef)({
        posthogClient: posthogClient,
        eventContext: eventContext,
        playingContentId: contentId,
        playingFeatureMediaId: featureMediaId,
        getPlaybackState: getPlaybackState,
        chatSessionId: chatSessionId,
        conversationTitle: conversationTitle,
        aiPersona: aiPersona,
        chatExperienceName: chatExperienceName,
    });
    contextRef.current = {
        posthogClient: posthogClient,
        eventContext: eventContext,
        playingContentId: contentId,
        playingFeatureMediaId: featureMediaId,
        getPlaybackState: getPlaybackState,
        chatSessionId: chatSessionId,
        conversationTitle: conversationTitle,
        aiPersona: aiPersona,
        chatExperienceName: chatExperienceName,
    };
    // Build the full callback map once per hook instance by spreading each
    // per-feature factory. Every callback reads from contextRef on invocation,
    // so the empty-deps useMemo is safe — values stay fresh through the ref.
    var callbacks = (0, react_1.useMemo)(function () { return (__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, (0, chat_1.createChatEventCallbacks)(contextRef)), (0, moderation_1.createModerationEventCallbacks)(contextRef)), (0, channels_1.createChannelEventCallbacks)(contextRef)), (0, divination_1.createDivinationEventCallbacks)(contextRef)), (0, playback_1.createPlaybackEventCallbacks)(contextRef)), (0, header_1.createHeaderEventCallbacks)(contextRef)), (0, pageViews_1.createPageViewEventCallbacks)(contextRef)), (0, profile_1.createProfileEventCallbacks)(contextRef)), (0, playlistLibrary_1.createPlaylistLibraryEventCallbacks)(contextRef)), (0, discover_1.createDiscoverEventCallbacks)(contextRef)), (0, sharedLink_1.createSharedLinkEventCallbacks)(contextRef))); }, []);
    return callbacks[event];
}
